import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import vaultsourceService from '../services/vaultsourceService';
import vaulttableService from '../services/vaulttableService';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import v2VaultSourceSettingsService from '../services/v2VaultSourceSettingsService';
import './VaultSourceForm.css';

function VaultSourceForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vaulttables, setVaultTables] = useState([]);
  const [vaulttablesCurrentPage, setVaultTablesCurrentPage] = useState(1);
  const vaulttablesPerPage = 20;
  const [vaulttablesSearchTerm, setVaultTablesSearchTerm] = useState('');
  const [vaultsourcegroups, setVaultSourceGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('vaulttables');
  const [settings, setSettings] = useState([]);
  const [settingForm, setSettingForm] = useState({ key: '', displayName: '', type: 'String', required: 'N' });
  const [editingSettingId, setEditingSettingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    vault_type: 'database_extract',
    api_cost_per_10k: ''
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchVaultSource();
      fetchVaultTables();
      fetchVaultSourceGroups();
      fetchSettings();
    }
  }, [id, isEditMode, isViewMode]);

  // Set correct default tab when vault_type is known
  useEffect(() => {
    if (formData.vault_type === 'api_extract' && activeTab === 'vaulttables') {
      setActiveTab('api_settings');
    }
  }, [formData.vault_type]);

  const fetchVaultSource = async () => {
    try {
      setLoading(true);
      const vaultsourceData = await vaultsourceService.getVaultSourceById(id);
      setFormData(vaultsourceData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vault system');
    } finally {
      setLoading(false);
    }
  };

  const fetchVaultTables = async () => {
    try {
      const response = await vaulttableService.getAllVaultTables({ vaultsourceid: id });
      setVaultTables(response.vaulttables || []);
    } catch (err) {
      console.error('Failed to fetch vault system tables:', err);
    }
  };

  const fetchVaultSourceGroups = async () => {
    try {
      const response = await vaultsourcegroupService.getAllVaultSourceGroups({ vaultsourceid: id });
      setVaultSourceGroups(response.vaultsourcegroups || []);
    } catch (err) {
      console.error('Failed to fetch vault system groups:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await v2VaultSourceSettingsService.getSettingsBySourceId(id);
      setSettings(response.settings || []);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const handleAddSetting = async () => {
    if (!settingForm.key.trim()) return;
    try {
      if (editingSettingId) {
        await v2VaultSourceSettingsService.updateSetting(editingSettingId, {
          setting_key: settingForm.key,
          display_name: settingForm.displayName,
          setting_type: settingForm.type,
          required_yn: settingForm.required
        });
        setEditingSettingId(null);
      } else {
        await v2VaultSourceSettingsService.createSetting({
          v2_vault_source_id: parseInt(id),
          setting_key: settingForm.key,
          display_name: settingForm.displayName,
          setting_type: settingForm.type,
          required_yn: settingForm.required
        });
      }
      setSettingForm({ key: '', displayName: '', type: 'String', required: 'N' });
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save setting');
    }
  };

  const handleEditSetting = (setting) => {
    setEditingSettingId(setting.id);
    setSettingForm({ key: setting.setting_key, displayName: setting.display_name || '', type: setting.setting_type || 'String', required: setting.required_yn || 'N' });
  };

  const handleDeleteSetting = async (settingId) => {
    if (!window.confirm('Delete this setting?')) return;
    try {
      await v2VaultSourceSettingsService.deleteSetting(settingId);
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete setting');
    }
  };

  const handleDeleteVaultTable = async (vaulttableId) => {
    if (!window.confirm('Are you sure you want to delete this vault table?')) {
      return;
    }

    try {
      await vaulttableService.deleteVaultTable(vaulttableId);
      // Refresh the vault system tables list
      fetchVaultTables();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault table');
    }
  };

  const handleToggleExtract = async (vaulttableId, currentValue) => {
    try {
      const newValue = currentValue === 'Y' ? 'N' : 'Y';

      // Find the vault table to get all its data
      const vaulttable = vaulttables.find(vt => vt.id === vaulttableId);
      if (!vaulttable) return;

      // Update the backend
      await vaulttableService.updateVaultTable(vaulttableId, {
        ...vaulttable,
        extract: newValue
      });

      // Refresh the vault system tables list
      fetchVaultTables();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update extract field');
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }


    try {
      console.log('Submitting vault system data:', formData);

      if (isCreateMode) {
        const response = await vaultsourceService.createVaultSource(formData);
        console.log('Vault source created:', response);
        // Navigate back to vault system list after create
        navigate('/admin/vaults');
      } else if (isEditMode) {
        const response = await vaultsourceService.updateVaultSource(id, formData);
        console.log('Vault source updated:', response);
        // Navigate to vault system view screen after update
        navigate(`/admin/vaults/${id}`);
      }
    } catch (err) {
      console.error('Error saving vault system:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save vault system');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/vaults');
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this vault system?')) {
      return;
    }

    try {
      setLoading(true);
      await vaultsourceService.deleteVaultSource(id);
      navigate('/admin/vaults');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault system');
      setLoading(false);
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="vaultsource-form-container">Loading vault system...</div>;
  }

  return (
    <div className="vaultsource-form-container">
      {/* Breadcrumb */}
      {(isViewMode || isEditMode) && formData.name && (
        <div className="breadcrumb">
          <Link to="/admin/vaults" className="breadcrumb-link">
            Vault Systems
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{formData.name}</span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="name">Vault source name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label>Vault type *</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="vault_type"
                  value="database_extract"
                  checked={formData.vault_type === 'database_extract'}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
                Database Extract
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="vault_type"
                  value="api_extract"
                  checked={formData.vault_type === 'api_extract'}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
                API Extract
              </label>
            </div>
          </div>

          {formData.vault_type === 'api_extract' && (
            <div className="form-group">
              <label htmlFor="api_cost_per_10k">API Cost per 10,000 Transactions (EUR)</label>
              <input
                type="number"
                id="api_cost_per_10k"
                name="api_cost_per_10k"
                value={formData.api_cost_per_10k || ''}
                onChange={handleChange}
                disabled={isViewMode}
                step="0.01"
                min="0"
                placeholder="e.g. 10.00"
              />
              <small style={{ color: '#6b7280', fontSize: '0.85em' }}>
                The cost charged per 10,000 API transactions (e.g. OHIP charges €10 per 10,000 requests). Leave blank if no additional cost.
              </small>
            </div>
          )}

        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/admin/vaults/${id}/edit`)} title="Edit Vault System">
                ✏️ Edit
              </button>
              <button type="button" onClick={handleDelete} className="btn-danger" title="Delete Vault System">
                🗑️ Delete
              </button>
              <button type="button" onClick={handleCancel} title="Back to List">
                ← Back to List
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Vault System' : 'Update Vault System'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading} title="Cancel">
                ← Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {/* Tabs Section - Only show in view/edit mode */}
      {(isViewMode || isEditMode) && id && (
        <div className="tabs-container">
          {/* Tab Navigation */}
          <div className="tabs-nav">
            {formData.vault_type === 'database_extract' && (
              <button
                className={`tab-button ${activeTab === 'vaulttables' ? 'active' : ''}`}
                onClick={() => setActiveTab('vaulttables')}
              >
                Vault System Tables
              </button>
            )}
            {formData.vault_type === 'api_extract' && (
              <button
                className={`tab-button ${activeTab === 'api_settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('api_settings')}
              >
                API Settings
              </button>
            )}
            <button
              className={`tab-button ${activeTab === 'vaultsourcegroups' ? 'active' : ''}`}
              onClick={() => setActiveTab('vaultsourcegroups')}
            >
              Vault System Groups
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Vault System Tables Tab */}
            {activeTab === 'vaulttables' && (
              <div className="vaulttables-section">
                <div className="vaulttables-header">
                  <Link to={`/admin/vaults/${id}/vaulttable/new`} className="btn-add-vaulttable" title="Add Vault System Table">
                    ➕ Add Vault System Table
                  </Link>
                </div>

                {/* Search Field */}
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search vault system tables..."
                    value={vaulttablesSearchTerm}
                    onChange={(e) => {
                      setVaultTablesSearchTerm(e.target.value);
                      setVaultTablesCurrentPage(1); // Reset to first page on search
                    }}
                    className="search-input"
                  />
                </div>

                {(() => {
                  // Filter vault system tables based on search term
                  const filteredVaultTables = vaulttables.filter((vaulttable) =>
                    vaulttable.name.toLowerCase().includes(vaulttablesSearchTerm.toLowerCase())
                  );

                  if (filteredVaultTables.length === 0) {
                    return (
                      <div className="table-container">
                        <table className="vaulttables-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Extract</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan="3" className="no-data">No vault system tables found.</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="table-container">
                        <table className="vaulttables-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Extract</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredVaultTables
                              .slice(
                                (vaulttablesCurrentPage - 1) * vaulttablesPerPage,
                                vaulttablesCurrentPage * vaulttablesPerPage
                              )
                              .map((vaulttable) => (
                                <tr
                                  key={vaulttable.id}
                                  className="clickable-row"
                                  onClick={() => navigate(`/admin/vaults/${id}/vaulttable/${vaulttable.id}`)}
                                  title="Click to view"
                                >
                                  <td className="table-name-cell">{vaulttable.name}</td>
                                  <td>
                                    <button
                                      className={`toggle-button ${vaulttable.extract === 'Y' ? 'toggle-yes' : 'toggle-no'}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleExtract(vaulttable.id, vaulttable.extract);
                                      }}
                                      title="Toggle Extract"
                                    >
                                      {vaulttable.extract === 'Y' ? 'Yes' : 'No'}
                                    </button>
                                  </td>
                                  <td className="actions-cell">
                                    <Link
                                      to={`/admin/vaults/${id}/vaulttable/${vaulttable.id}/edit`}
                                      className="btn-action btn-edit-action"
                                      onClick={(e) => e.stopPropagation()}
                                      title="Edit"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                      </svg>
                                    </Link>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteVaultTable(vaulttable.id);
                                      }}
                                      className="btn-action btn-delete-action"
                                      title="Delete"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination controls */}
                      {filteredVaultTables.length > vaulttablesPerPage && (
                        <div className="pagination">
                          <button
                            onClick={() => setVaultTablesCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={vaulttablesCurrentPage === 1}
                          >
                            Previous
                          </button>
                          <span>
                            Page {vaulttablesCurrentPage} of {Math.ceil(filteredVaultTables.length / vaulttablesPerPage)}
                          </span>
                          <button
                            onClick={() =>
                              setVaultTablesCurrentPage((prev) =>
                                Math.min(Math.ceil(filteredVaultTables.length / vaulttablesPerPage), prev + 1)
                              )
                            }
                            disabled={vaulttablesCurrentPage >= Math.ceil(filteredVaultTables.length / vaulttablesPerPage)}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* API Settings Tab - api_extract only */}
            {activeTab === 'api_settings' && formData.vault_type === 'api_extract' && (
              <div className="vaulttables-section">
                {!isViewMode && (
                  <div className="settings-form">
                    <div className="settings-form-row">
                      <input
                        type="text"
                        placeholder="Setting key (e.g. client_id)"
                        value={settingForm.key}
                        onChange={(e) => setSettingForm(prev => ({ ...prev, key: e.target.value }))}
                        className="search-input"
                      />
                      <input
                        type="text"
                        placeholder="Display name (e.g. Client ID)"
                        value={settingForm.displayName}
                        onChange={(e) => setSettingForm(prev => ({ ...prev, displayName: e.target.value }))}
                        className="search-input"
                      />
                      <select
                        value={settingForm.type}
                        onChange={(e) => setSettingForm(prev => ({ ...prev, type: e.target.value }))}
                        className="search-input"
                      >
                        <option value="String">String</option>
                        <option value="Number">Number</option>
                        <option value="Date">Date</option>
                      </select>
                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={settingForm.required === 'Y'}
                          onChange={(e) => setSettingForm(prev => ({ ...prev, required: e.target.checked ? 'Y' : 'N' }))}
                        />
                        Required
                      </label>
                      <button type="button" onClick={handleAddSetting} className="btn-add-vaulttable">
                        {editingSettingId ? 'Update' : '➕ Add'}
                      </button>
                      {editingSettingId && (
                        <button type="button" onClick={() => { setEditingSettingId(null); setSettingForm({ key: '', displayName: '', type: 'String', required: 'N' }); }} className="btn-add-vaulttable" style={{ borderColor: '#9ca3af', color: '#9ca3af' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <div className="table-container">
                  <table className="vaulttables-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Display Name</th>
                        <th>Type</th>
                        <th>Required</th>
                        {!isViewMode && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {settings.length === 0 ? (
                        <tr><td colSpan={isViewMode ? 4 : 5} className="no-data">No settings configured.</td></tr>
                      ) : (
                        settings.map((setting) => (
                          <tr key={setting.id}>
                            <td className="table-name-cell">{setting.setting_key}</td>
                            <td>{setting.display_name || ''}</td>
                            <td>{setting.setting_type || 'String'}</td>
                            <td>{setting.required_yn === 'Y' ? 'Yes' : 'No'}</td>
                            {!isViewMode && (
                              <td className="actions-cell">
                                <button
                                  type="button"
                                  className="btn-action btn-edit-action"
                                  onClick={() => handleEditSetting(setting)}
                                  title="Edit"
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="btn-action btn-delete-action"
                                  onClick={() => handleDeleteSetting(setting.id)}
                                  title="Delete"
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Vault System Groups Tab - both vault types */}
            {activeTab === 'vaultsourcegroups' && (
              <div className="vaultsourcegroups-section">
                <div className="vaultsourcegroups-header">
                  <Link to={`/admin/vaults/${id}/vaultsourcegroup/new`} className="btn-add-vaultsourcegroup" title="Add Vault System Group">
                    ➕ Add Vault System Group
                  </Link>
                </div>
                <div className="table-container">
                  <table className="vaultsourcegroups-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vaultsourcegroups.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="no-data">No vault system groups found.</td>
                        </tr>
                      ) : (
                        vaultsourcegroups.map((group) => (
                          <tr
                            key={group.id}
                            className="clickable-row"
                            onClick={() => navigate(`/admin/vaults/${id}/vaultsourcegroup/${group.id}`)}
                            title="Click to view"
                          >
                            <td className="table-name-cell">{group.name}</td>
                            <td className="actions-cell">
                              <Link
                                to={`/admin/vaults/${id}/vaultsourcegroup/${group.id}/edit`}
                                className="btn-action btn-edit-action"
                                onClick={(e) => e.stopPropagation()}
                                title="Edit"
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VaultSourceForm;
